import { Bound } from '@blocksuite/hive/global/gfx';
import { nanoid } from '@blocksuite/hive/store';

import { AIProvider } from '../provider';

const replaceText = (text: Record<string, string>, template: any) => {
  if (template != null && typeof template === 'object') {
    if (Array.isArray(template)) {
      template.forEach(v => replaceText(text, v));
      return;
    }
    if (typeof template.insert === 'string') {
      template.insert = text[template.insert] ?? template.insert;
    }
    Object.values(template).forEach(v => replaceText(text, v));
    return;
  }
};

const getCaptionSet = (template: any) => {
  const captions = new Set<string>();
  const run = (data: any) => {
    if (data != null && typeof data === 'object') {
      if (Array.isArray(data)) {
        data.forEach(v => run(v));
        return;
      }
      if (typeof data.caption === 'string') {
        captions.add(data.caption);
      }
      Object.values(data).forEach(v => run(v));
    }
  };
  run(template);
  return captions;
};

const hasCaptions = (template: any, captions: string[]) => {
  const existing = getCaptionSet(template);
  return captions.every(caption => existing.has(caption));
};

const findBlock = (
  block: any,
  predicate: (block: any) => boolean
): any | null => {
  if (!block || typeof block !== 'object') return null;
  if (predicate(block)) return block;

  for (const child of block.children ?? []) {
    const found = findBlock(child, predicate);
    if (found) return found;
  }

  return null;
};

const createImageBounds = (frameBound: Bound, count: number) => {
  const margin = Math.max(48, frameBound.w * 0.04);
  const gap = Math.max(24, frameBound.h * 0.025);
  const width = Math.min(420, frameBound.w * 0.28);
  const height = Math.min(
    frameBound.h * 0.22,
    (frameBound.h - margin * 2 - gap * (count - 1)) / count
  );
  const totalHeight = height * count + gap * (count - 1);
  const x = frameBound.x + frameBound.w - width - margin;
  const startY = frameBound.y + (frameBound.h - totalHeight) / 2;

  return Array.from(
    { length: count },
    (_, index) => new Bound(x, startY + index * (height + gap), width, height)
  );
};

const ensureImagePlaceholders = (template: any, captions: string[]) => {
  const existing = getCaptionSet(template);
  const missing = captions.filter(caption => !existing.has(caption));
  if (!missing.length) return;

  const surface = findBlock(
    template.blocks,
    block => block.flavour === 'hive:surface'
  );
  const frame = findBlock(
    template.blocks,
    block => block.flavour === 'hive:frame'
  );
  if (!surface || !frame?.props?.xywh) return;

  surface.children ??= [];
  frame.props.childElementIds ??= {};

  const bounds = createImageBounds(
    Bound.deserialize(frame.props.xywh),
    missing.length
  );
  missing.forEach((caption, index) => {
    const id = nanoid();
    const bound = bounds[index];
    surface.children.push({
      type: 'block',
      id,
      flavour: 'hive:image',
      version: 1,
      props: {
        caption,
        sourceId: '',
        width: Math.round(bound.w),
        height: Math.round(bound.h),
        index: `az${index}`,
        xywh: bound.serialize(),
        rotate: 0,
        size: -1,
      },
      children: [],
    });
    frame.props.childElementIds[id] = true;
  });
};

function seededRNG(seed: string) {
  const a = 1664525;
  const c = 1013904223;
  const m = 2 ** 32;
  const seededNumber = stringToNumber(seed);
  return ((a * seededNumber + c) % m) / m;

  function stringToNumber(str: string) {
    let res = 0;
    for (let i = 0; i < str.length; i++) {
      const character = str.charCodeAt(i);
      res += character;
    }
    return res;
  }
}

const getFallbackImageUrl = (keyword: string, w: number, h: number) => {
  const query =
    keyword
      .split(/[\s,]+/)
      .map(word => word.trim().toLowerCase())
      .filter(Boolean)
      .slice(0, 3)
      .join(',') || 'business,presentation';
  const width = Math.max(1, Math.round(w || 800));
  const height = Math.max(1, Math.round(h || 600));
  const lock = Math.floor(seededRNG(`${query}.${width}.${height}`) * 1000000);

  return `https://loremflickr.com/${width}/${height}/${encodeURIComponent(
    query
  )}?lock=${lock}`;
};

const getImageUrlByKeyword =
  (keyword: string) =>
  async (w: number, h: number): Promise<string> => {
    const photos = await AIProvider.photoEngine?.searchImages({
      query: keyword,
      width: w,
      height: h,
    });

    if (photos == null || photos.length === 0) {
      return getFallbackImageUrl(keyword, w, h);
    }

    // use a stable random seed
    const rng = seededRNG(`${w}.${h}.${keyword}`);
    return photos[Math.floor(rng * photos.length)];
  };

const getImages = async (
  images: Record<string, (w: number, h: number) => Promise<string> | string>,

  template: any
): Promise<TemplateImage[]> => {
  const imgs: Record<
    string,
    {
      id: string;
      width: number;
      height: number;
    }
  > = {};

  const run = (data: any) => {
    if (data != null && typeof data === 'object') {
      if (Array.isArray(data)) {
        data.forEach(v => run(v));
        return;
      }
      if (typeof data.caption === 'string') {
        const bound = Bound.deserialize(data.xywh);
        const id = nanoid();
        data.sourceId = id;
        imgs[data.caption] = {
          id: id,
          width: bound.w,
          height: bound.h,
        };
        delete data.caption;
      }
      Object.values(data).forEach(v => run(v));
      return;
    }
  };
  run(template);
  const list = await Promise.all(
    Object.entries(imgs).map(async ([name, data]) => {
      const getImage = images[name];
      if (!getImage) {
        return null;
      }
      try {
        const url = await getImage(data.width, data.height);
        if (!url) {
          return null;
        }
        return {
          id: data.id,
          url,
        } as TemplateImage;
      } catch (error) {
        console.error('Error getting image:', error);
        return null;
      }
    })
  );
  return list.filter(v => !!v);
};

export type PPTSection = {
  title: string;
  content: string;
  keywords: string;
};

export type TemplateImage = {
  id: string;
  url: string;
};

type DocTemplate = {
  images: TemplateImage[];
  content: unknown;
};

const createBasicCover = async (
  title: string,
  section1: PPTSection
): Promise<DocTemplate> => {
  const templates = (await import('./templates/cover.json')).default;
  const template = getRandomElement(templates, ['background']);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        background: getImageUrlByKeyword(section1.keywords),
      },
      template
    ),
    content: template,
  };
};

function getRandomElement<T>(arr: T[], captions: string[] = []): T {
  const candidates = captions.length
    ? arr.filter(template => hasCaptions(template, captions))
    : arr;
  const source = candidates.length ? candidates : arr;
  return JSON.parse(
    JSON.stringify(source[Math.floor(Math.random() * source.length)])
  );
}

const basic1section = async (
  title: string,
  section1: PPTSection
): Promise<DocTemplate> => {
  const templates = (await import('./templates/one.json')).default;
  const imageCaptions = ['section1.image'];
  const template = getRandomElement(templates, imageCaptions);
  ensureImagePlaceholders(template, imageCaptions);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
    },
    template
  );
  const images = await getImages(
    {
      'section1.image': getImageUrlByKeyword(section1.keywords),
      'section1.image2': getImageUrlByKeyword(section1.keywords),
      'section1.image3': getImageUrlByKeyword(section1.keywords),
    },
    template
  );

  return {
    images: images,
    content: template,
  };
};

const basic2section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection
): Promise<DocTemplate> => {
  const templates = (await import('./templates/two.json')).default;
  const imageCaptions = ['section1.image', 'section2.image'];
  const template = getRandomElement(templates, imageCaptions);
  ensureImagePlaceholders(template, imageCaptions);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': getImageUrlByKeyword(section1.keywords),
        'section2.image': getImageUrlByKeyword(section2.keywords),
        background: () =>
          '/ppt-images/background/basic_2_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

const basic3section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection,
  section3: PPTSection
): Promise<DocTemplate> => {
  const templates = (await import('./templates/three.json')).default;
  const imageCaptions = ['section1.image', 'section2.image', 'section3.image'];
  const template = getRandomElement(templates, imageCaptions);
  ensureImagePlaceholders(template, imageCaptions);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
      'section3.title': section3.title,
      'section3.content': section3.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': getImageUrlByKeyword(section1.keywords),
        'section2.image': getImageUrlByKeyword(section2.keywords),
        'section3.image': getImageUrlByKeyword(section3.keywords),
        background: () =>
          '/ppt-images/background/basic_3_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

const basic4section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection,
  section3: PPTSection,
  section4: PPTSection
): Promise<DocTemplate> => {
  const templates = (await import('./templates/four.json')).default;
  const imageCaptions = [
    'section1.image',
    'section2.image',
    'section3.image',
    'section4.image',
  ];
  const template = getRandomElement(templates, imageCaptions);
  ensureImagePlaceholders(template, imageCaptions);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
      'section3.title': section3.title,
      'section3.content': section3.content,
      'section4.title': section4.title,
      'section4.content': section4.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': getImageUrlByKeyword(section1.keywords),
        'section2.image': getImageUrlByKeyword(section2.keywords),
        'section3.image': getImageUrlByKeyword(section3.keywords),
        'section4.image': getImageUrlByKeyword(section4.keywords),
        background: () =>
          '/ppt-images/background/basic_4_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

export type PPTDoc = {
  isCover: boolean;
  title: string;
  sections: PPTSection[];
};

export const basicTheme = (doc: PPTDoc) => {
  if (doc.isCover) {
    return createBasicCover(doc.title, doc.sections[0]);
  }
  if (doc.sections.length === 1) {
    return basic1section(doc.title, doc.sections[0]);
  }
  if (doc.sections.length === 2) {
    return basic2section(doc.title, doc.sections[0], doc.sections[1]);
  }
  if (doc.sections.length === 3) {
    return basic3section(
      doc.title,
      doc.sections[0],
      doc.sections[1],
      doc.sections[2]
    );
  }
  return basic4section(
    doc.title,
    doc.sections[0],
    doc.sections[1],
    doc.sections[2],
    doc.sections[3]
  );
};
